import type { Options, Shape } from 'canvas-confetti'

type ConfettiModule = typeof import('canvas-confetti')

const COLORS = ['#e8547c', '#f3c77f', '#ffffff', '#fda4bc', '#c22d5a', '#ffd1dc']
const HEART_PATH = 'M12 21s-7.5-4.6-9.6-9.2C.9 8.3 3 4.5 6.8 4.5c2.2 0 3.7 1.2 5.2 3 1.5-1.8 3-3 5.2-3 3.8 0 5.9 3.8 4.4 7.3C19.5 16.4 12 21 12 21z'

let loader: Promise<ConfettiModule['default']> | null = null
let heartShape: Shape | null = null

const loadConfetti = (): Promise<ConfettiModule['default']> => {
    if (!loader) loader = import('canvas-confetti').then(module => module.default)

    return loader
}

const prefersReducedMotion = (): boolean => {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const getHeart = (confetti: ConfettiModule['default']): Shape | null => {
    if (heartShape) return heartShape

    try {
        heartShape = confetti.shapeFromPath({ path: HEART_PATH })
    }
    catch {
        heartShape = null
    }

    return heartShape
}

const between = (min: number, max: number): number => Math.random() * (max - min) + min

const base = (extra: Options): Options => ({
    colors: COLORS,
    disableForReducedMotion: true,
    zIndex: 90,
    ...extra
})

/** Two cannons of confetti and hearts from both sides of the screen. */
export async function burstConfetti(): Promise<void> {
    if (prefersReducedMotion()) return

    const confetti = await loadConfetti()
    const heart = getHeart(confetti)
    const shapes: Array<Shape> = heart ? ['circle', 'square', heart] : ['circle', 'square']

    confetti(base({ particleCount: 80, angle: 60, spread: 65, startVelocity: 52, origin: { x: 0, y: 0.75 }, shapes }))
    confetti(base({ particleCount: 80, angle: 120, spread: 65, startVelocity: 52, origin: { x: 1, y: 0.75 }, shapes }))
}

/** A gentle shower of hearts falling from the top. */
export async function heartShower(): Promise<void> {
    if (prefersReducedMotion()) return

    const confetti = await loadConfetti()
    const heart = getHeart(confetti)

    if (!heart) return

    for (let index = 0; index < 4; index += 1) {
        window.setTimeout(() => {
            confetti(base({
                particleCount: 24,
                spread: 120,
                startVelocity: 18,
                gravity: 0.6,
                scalar: 1.6,
                ticks: 260,
                shapes: [heart],
                origin: { x: between(0.15, 0.85), y: -0.1 }
            }))
        }, index * 350)
    }
}

/** Fireworks bursting across the sky for a few seconds. */
export async function fireworks(duration = 6000): Promise<void> {
    if (prefersReducedMotion()) return

    const confetti = await loadConfetti()
    const heart = getHeart(confetti)
    const end = Date.now() + duration

    const timer = window.setInterval(() => {
        const timeLeft = end - Date.now()

        if (timeLeft <= 0) {
            window.clearInterval(timer)

            return
        }

        const particleCount = Math.round(40 * (timeLeft / duration)) + 18
        const shapes: Array<Shape> = heart && Math.random() > 0.5 ? [heart, 'circle'] : ['circle', 'star']

        confetti(base({ particleCount, spread: 360, startVelocity: 32, ticks: 80, gravity: 0.9, shapes, origin: { x: between(0.1, 0.35), y: between(0.1, 0.45) } }))
        confetti(base({ particleCount, spread: 360, startVelocity: 32, ticks: 80, gravity: 0.9, shapes, origin: { x: between(0.65, 0.9), y: between(0.1, 0.45) } }))
    }, 320)
}
